import { defineStore } from 'pinia';

import { fetchUserInfo as fetchUserInfoAPI, fetchWebTokenResponse } from '@/api/apiService';
import api from '@/api/index';
import { useCookie } from '@/composables/shared/useCookie';
import dayjs from '@/plugins/dayjs.js';
import { debugError } from '@/static/develop.js';
import { getBrowserLanguage } from '@/static/languages';
import { BREAKPOINTS } from '@/stores/breakpoints';
import { LanguageCode } from '@/types/common';
import { IResponseUserCountry } from '@/types/response';
import { CHAT_TEXT_MULTIPLIERS } from '@/utils/constants/chat';
import { getBrowserAndDeviceInfo, isInCapacitorApp } from '@/utils/device';
import { base64UrlSafeToString } from '@/utils/encryptor.js';
import { secureStorageSet } from '@/utils/native/nativeBridge';

export const UserType = Object.freeze({
  GUEST: 'GUEST',
  SUB: 'SUB', // 호스트
  HOST: 'HOST', // 오너호스트
  isPartner: function (type) {
    return type && type !== this.GUEST;
  },
  isUser: function (type) {
    return type && type === this.GUEST;
  },
  isHost: function (type) {
    return type && type === this.HOST;
  },
});

export const useUserStore = defineStore('user', () => {
  const { locale } = useI18n();
  const { removeCookie } = useCookie();

  const authToken = ref(localStorage.getItem('authToken') || null);
  const userId = ref(localStorage.getItem('userId') || null);
  const userType = ref(localStorage.getItem('userType') || null);
  const isTester = ref(localStorage.getItem('isTester') || null);
  const partnerId = ref(localStorage.getItem('partnerId') || null);
  const initializedPassword = ref(localStorage.getItem('initializedPassword') || null);

  const pageLanguage = ref<LanguageCode>(localStorage.getItem('locale') || getBrowserLanguage());
  const callLanguage = ref(null);

  // 사용자 정보
  const userInfo = ref(JSON.parse(localStorage.getItem('userInfo')) || null);
  // 사용자 설정
  const userPreferences = ref({
    isActiveMyLanguageOnly: true, // 나의 언어만 보기
    isActiveTextLarger: true, // 큰 글씨로 보기
    textSizeMultiplier: 1.0, // 글자 크기 배율
    screenLanguageCode: null,
    screenMessageWidth: 95, // 스크린 모드 메세지 너비
    screenScrollHeight: 50, // 스크린 모드 메세지 높이
    screenContainerHeight: 500 / 16, // GSC 스크린 모드 컨테이너 고정 높이
    subtitleLanguageCode: null,
    subtitleOpacity: 0.8, // 자막 투명도
    subtitleLocationY: 1, // 자막 위치 Y축
    subtitleLineHeight: 1.5, // 자막 줄간격
    subtitleLineCount: 3, // 자막 줄 수
    subtitleWidth: 99, // 자막 너비
    subtitleBackgroundColor: 'GREEN', // 자막 배경색
    subtitleIsVisibleLogo: true, // 자막 로고 노출 여부
    subtitleIsSettingAbove: true,
    voiceSize: 0, // 음성 구분 조절
  });

  const remotePreferences = ref({
    isActiveMyLanguageOnly: true,
    isActiveTextLarger: true,
    voiceSize: 0,
    screenMessageWidth: 95,
    screenScrollHeight: 50,
    screenContainerHeight: 500 / 16,
    textSizeMultiplier: 1.0,
    subtitleOpacity: 0.8,
    subtitleLocationY: 1,
    subtitleLineHeight: 1.5,
    subtitleLineCount: 3,
    subtitleWidth: 99,
    subtitleBackgroundColor: 'GREEN',
    subtitleIsVisibleLogo: true,
    subtitleIsSettingAbove: true,
  });

  const isLoading = ref(false);
  const error = ref(null);
  const loginType = ref(localStorage.getItem('loginType'));
  const isPendingLogout = ref<boolean>(false);

  const countryList = ref<IResponseUserCountry[]>([]);

  const isAuthenticated = computed(() => !!authToken.value);

  // 사용자 장비 정보
  const userDeviceInfo = computed(() => {
    const deviceInfo = getBrowserAndDeviceInfo();
    deviceInfo['isInApp'] = isInCapacitorApp();
    return deviceInfo;
  });

  // 아이폰 여부
  const isIos = computed(() => {
    return userDeviceInfo.value.os === 'iOS';
  });

  // 아이폰 여부
  const isAndroid = computed(() => {
    return userDeviceInfo.value.os === 'Android';
  });

  // 핸드폰 여부
  const isMobile = computed(() => {
    return userDeviceInfo.value.deviceType === 'Mobile';
  });

  const isTablet = computed(() => {
    const isBiggerThanMobile = window.innerWidth >= BREAKPOINTS.tablet;
    const isSmallerThanDesktop = window.innerWidth <= BREAKPOINTS.desktop;
    return isBiggerThanMobile && isSmallerThanDesktop;
  });

  // 🌟 Actions (Methods)
  const setLoginInfo = async (info) => {
    const {
      userId: id,
      token,
      userType: type,
      isTester: test,
      partnerId: partner,
      initializedPassword: initPassword,
      loginType: typeLogin,
      permissions,
      partnerCode = '',
      refreshToken,
    } = info;

    userId.value = id;
    authToken.value = token;
    userType.value = type;
    isTester.value = test;
    partnerId.value = partner;
    initializedPassword.value = initPassword;
    loginType.value = typeLogin;

    if (isInCapacitorApp()) {
      await secureStorageSet({ key: 'authToken', value: token });
      if (refreshToken) {
        await secureStorageSet({ key: 'refreshToken', value: refreshToken });
      }
      if (partnerCode) {
        await secureStorageSet({ key: 'partnerCode', value: partnerCode });
      }
    }
    // Save to localStorage
    localStorage.setItem('authToken', token);
    sessionStorage.setItem('authToken', token);
    localStorage.setItem('userId', id);
    localStorage.setItem('userType', type);
    localStorage.setItem('isTester', test);
    localStorage.setItem('partnerId', partner);
    localStorage.setItem('initializedPassword', initPassword);
    localStorage.setItem('permissions', JSON.stringify(permissions));
    localStorage.setItem('loginType', typeLogin);
    localStorage.setItem(typeLogin === 'DISTRIBUTOR' ? 'partner-code-dist' : 'partner-code-admin', partnerCode);

    api.defaults.headers.common['Authorization'] = token;
  };

  // 페이지 언어 설정
  const setPageLanguage = (newLanguage) => {
    dayjs.locale(newLanguage === 'ko' ? 'ko' : 'en');
    pageLanguage.value = newLanguage;
  };

  // 사용자 쿠키 설정
  const setUserCookie = async (id) => {
    try {
      const response = await fetchWebTokenResponse(id);
      const { key, value } = response.data;
      localStorage.setItem(key, value);
    } catch (err) {
      debugError('Setting user cookie failed:', err);
    }
  };

  // 사용자 정보 조회
  const fetchUserInfo = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await fetchUserInfoAPI();
      userInfo.value = {
        ...response.data,
        nickname: base64UrlSafeToString(response.data.nickname),
      };
      userId.value = response.data.userId;
      partnerId.value = response.data.partnerId;
      localStorage.setItem('userInfo', JSON.stringify(userInfo.value));
      localStorage.setItem('userId', response.data.userId);
      localStorage.setItem('partnerId', response.data.partnerId);

      // Handle webTokenResponse
      const webTokenResponse = response.data.webTokenResponse;
      if (webTokenResponse) {
        localStorage.setItem(webTokenResponse.key, webTokenResponse.value);
      }

      isLoading.value = false;
      return response.data;
    } catch (err) {
      error.value = err;
      isLoading.value = false;
      debugError('Failed to fetch user info:', err);
      throw err;
    }
  };

  // 로그아웃 처리
  const requestLogout = async () => {
    try {
      await api.post(`/api/partner/auth/logout`);
    } catch (error) {
      console.error('로그아웃 중 오류 발생:', error);
    } finally {
      const keysToRemove = ['parnterId', 'userId', 'authToken', 'userType', 'isTester', 'SC-USER-ID'];
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      sessionStorage.removeItem('authToken');
      userId.value = null;
      userInfo.value = null;
      removeCookie('refreshToken');
    }

    return true;
  };

  // 사용자 설정값 수정
  const updateUserPreferences = ({ setting, newVal }) => {
    userPreferences.value[setting] = newVal;
  };

  const assimilateUserPreferences = (newVal) => {
    const incomingTextSize = parseFloat(newVal.textSizeMultiplier);
    const isTooBig = incomingTextSize > CHAT_TEXT_MULTIPLIERS[9];
    const isTooSmall = incomingTextSize < CHAT_TEXT_MULTIPLIERS[0];

    if (isTooBig || isTooSmall) {
      const fallBack = isTooSmall ? CHAT_TEXT_MULTIPLIERS[0] : CHAT_TEXT_MULTIPLIERS[9];
      newVal.textSizeMultiplier = CHAT_TEXT_MULTIPLIERS[Math.round(incomingTextSize)] ?? fallBack;
    }

    userPreferences.value = { ...userPreferences.value, ...newVal };
  };

  // 글자 크기 조절
  const setTextMultiplier = (newVal: number) => {
    userPreferences.value.textSizeMultiplier = CHAT_TEXT_MULTIPLIERS[newVal];
  };

  const setupRemotedUserPreferences = (type: string) => {
    const cachedSettings = localStorage.getItem(type);

    if (!cachedSettings) return null;

    const parsedData = JSON.parse(cachedSettings);
    const isScreen = type.indexOf('screen') > -1;
    const attributeData = isScreen ? parsedData['screenAttribute'] : parsedData['subtitleAttribute'];
    const targetLanguageCode = isScreen ? 'screenLanguageCode' : 'subtitleLanguageCode';

    userPreferences.value = {
      ...userPreferences.value,
      ...attributeData,
      [targetLanguageCode]: attributeData.languageCode,
    };
  };

  const setupUserPreferences = (type: string) => {
    const cachedSettings = localStorage.getItem(type);

    if (!cachedSettings) return null;

    const parsedData = JSON.parse(cachedSettings);
    const isScreen = type.indexOf('screen') > -1;
    const targetLanguageCode = isScreen ? 'screenLanguageCode' : 'subtitleLanguageCode';

    userPreferences.value = { ...userPreferences.value, ...parsedData, [targetLanguageCode]: parsedData.languageCode };
  };

  watch(
    pageLanguage,
    (newLang) => {
      locale.value = newLang;
      localStorage.setItem('locale', newLang);
    },
    { immediate: true }
  );

  watch(
    remotePreferences,
    (newVal) => {
      if (newVal) {
        userPreferences.value = { ...userPreferences.value, ...newVal };
      }
    },
    { immediate: true, deep: true }
  );

  return {
    // State
    authToken,
    userId,
    userType,
    isTester,
    partnerId,
    initializedPassword,
    pageLanguage,
    callLanguage,
    userInfo,
    isLoading,
    error,
    userDeviceInfo,
    userPreferences,
    remotePreferences,
    isPendingLogout,
    // Computed
    isAuthenticated,
    isIos,
    isAndroid,
    isMobile,
    isTablet,
    // Actions
    setLoginInfo,
    setPageLanguage,
    setUserCookie,
    fetchUserInfo,
    requestLogout,
    updateUserPreferences,
    assimilateUserPreferences,
    setTextMultiplier,
    setupRemotedUserPreferences,
    setupUserPreferences,
  };
});
